import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import { Add as AddIcon, Phone as PhoneIcon, Email as EmailIcon, Event as EventIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../providers/TenantProvider';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Lead {
  id: string;
  tenantId: string;
  contact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string | null;
  };
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  pipelineStage: PipelineStage;
  source?: string | null;
  notes?: string | null;
  tags?: string[];
  nextActionAt?: { toDate?: () => Date } | string | null;
  nextActionDate?: string | null;
  estimatedValue?: number | null;
  createdAt: any;
  updatedAt: any;
}

type PipelineStage = 'new' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';

const PIPELINE_STAGES: Array<{ id: PipelineStage; label: string; color: string }> = [
  { id: 'new', label: 'New', color: '#9e9e9e' },
  { id: 'contacted', label: 'Contacted', color: '#2196f3' },
  { id: 'qualified', label: 'Qualified', color: '#ff9800' },
  { id: 'proposal', label: 'Proposal', color: '#9c27b0' },
  { id: 'negotiation', label: 'Negotiation', color: '#f44336' },
  { id: 'won', label: 'Won', color: '#4caf50' },
  { id: 'lost', label: 'Lost', color: '#424242' },
];

function getLeadContact(lead: Lead) {
  return {
    firstName: lead.contact?.firstName || lead.firstName || '',
    lastName: lead.contact?.lastName || lead.lastName || '',
    email: lead.contact?.email || lead.email || '',
    phone: lead.contact?.phone || lead.phone || '',
  };
}

function getLeadNextActionDate(lead: Lead): Date | null {
  const value = lead.nextActionAt || lead.nextActionDate;
  if (!value) return null;
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

interface LeadCardProps {
  lead: Lead;
  onClick: (leadId: string) => void;
}

function LeadCard({ lead, onClick }: LeadCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lead.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const contact = getLeadContact(lead);
  const nextActionDate = getLeadNextActionDate(lead);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        mb: 1,
        height: 126,
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <CardActionArea onClick={() => onClick(lead.id)} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 1.5, height: '100%', overflow: 'hidden' }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" fontWeight="bold" noWrap>
              {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unnamed Lead'}
            </Typography>
            {contact.email && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <EmailIcon fontSize="small" sx={{ fontSize: 14 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {contact.email}
                </Typography>
              </Stack>
            )}
            {contact.phone && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PhoneIcon fontSize="small" sx={{ fontSize: 14 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {contact.phone}
                </Typography>
              </Stack>
            )}
            {nextActionDate && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <EventIcon fontSize="small" sx={{ fontSize: 14 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {nextActionDate.toLocaleDateString()}
                </Typography>
              </Stack>
            )}
            {lead.estimatedValue && (
              <Typography variant="caption" color="primary" fontWeight="bold">
                ${lead.estimatedValue.toLocaleString()}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function LeadsPipelineBoard() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    source: '',
    notes: '',
    estimatedValue: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (!tenantId) return;

    const q = query(
      collection(db, 'leads'),
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leadsData: Lead[] = [];
      snapshot.forEach((doc) => {
        leadsData.push({ id: doc.id, ...doc.data() } as Lead);
      });
      setLeads(leadsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [tenantId]);

  const handleCreateLead = async () => {
    if (!tenantId || !auth.currentUser) return;

    try {
      const leadData = {
        tenantId,
        contact: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
        },
        source: formData.source.trim() || null,
        notes: formData.notes.trim(),
        tags: [],
        pipelineStage: 'new' as PipelineStage,
        estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null,
        nextActionAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser.uid,
      };

      await addDoc(collection(db, 'leads'), leadData);
      setCreateDialogOpen(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        source: '',
        notes: '',
        estimatedValue: '',
      });
    } catch (error) {
      console.error('Error creating lead:', error);
      alert('Failed to create lead. Please try again.');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    // Check if dropped on a stage column
    const newStage = over.id as PipelineStage;
    const lead = leads.find((l) => l.id === active.id);

    if (lead && PIPELINE_STAGES.some((s) => s.id === newStage)) {
      try {
        const leadRef = doc(db, 'leads', lead.id);
        await updateDoc(leadRef, {
          pipelineStage: newStage,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('Error updating lead stage:', error);
        alert('Failed to move lead. Please try again.');
      }
    }
  };

  const getLeadsByStage = (stage: PipelineStage) => {
    return leads.filter((lead) => lead.pipelineStage === stage);
  };

  const activeLead = activeId ? leads.find((l) => l.id === activeId) : null;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading leads...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }} 
        sx={{ p: 3, pb: 2, gap: 2 }}
      >
        <Typography variant="h4">Sales Pipeline</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Add Lead
        </Button>
      </Stack>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ 
          flex: 1, 
          overflowX: 'auto', 
          overflowY: 'hidden',
          px: 3,
          pb: 3,
        }}>
          <Stack 
            direction="row" 
            spacing={2} 
            sx={{ 
              height: '100%',
              minWidth: 'min-content',
            }}
          >
            {PIPELINE_STAGES.map((stage) => {
              const stageLeads = getLeadsByStage(stage.id);
              const totalValue = stageLeads.reduce((sum, lead) => sum + (lead.estimatedValue || 0), 0);

              return (
                <SortableContext
                  key={stage.id}
                  id={stage.id}
                  items={stageLeads.map((l) => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Paper
                    sx={{
                      minWidth: 280,
                      maxWidth: 'calc((100vw - 260px - 80px) / 4)', // 260px sidebar + padding, max 4 columns
                      width: 'calc((100vw - 260px - 80px) / 4)',
                      bgcolor: 'grey.50',
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Stack>
                      <Typography variant="h6" sx={{ color: stage.color, fontWeight: 'bold' }}>
                        {stage.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stageLeads.length} lead{stageLeads.length !== 1 ? 's' : ''}
                      </Typography>
                    </Stack>
                    {totalValue > 0 && (
                      <Chip
                        label={`$${totalValue.toLocaleString()}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    {stageLeads.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onClick={(leadId) => navigate(`/leads/${leadId}`)}
                      />
                    ))}
                  </Box>
                </Paper>
              </SortableContext>
            );
          })}
          </Stack>
        </Box>

        <DragOverlay>
          {activeLead ? (
            <Card sx={{ width: 280, opacity: 0.9 }}>
              <CardContent sx={{ p: 1.5 }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {[getLeadContact(activeLead).firstName, getLeadContact(activeLead).lastName].filter(Boolean).join(' ') || 'Unnamed Lead'}
                  </Typography>
                  {getLeadContact(activeLead).email && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {getLeadContact(activeLead).email}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Lead</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, firstName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, lastName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Source"
              value={formData.source}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, source: e.target.value })}
              placeholder="e.g., Website, Referral, Walk-in"
              fullWidth
            />
            <TextField
              label="Estimated Value"
              type="number"
              value={formData.estimatedValue}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, estimatedValue: e.target.value })}
              placeholder="Monthly membership value"
              InputProps={{ startAdornment: '$' }}
              fullWidth
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateLead}
            variant="contained"
            disabled={!formData.firstName || !formData.lastName || !formData.email}
          >
            Create Lead
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
